import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import VehicleCard from "@/components/site/VehicleCard";
import { api, gbp } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useSite } from "@/lib/site";

const PRICES = [15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000, 60000].map((p) => ({ value: p, label: gbp(p) }));
const MONTHLY = [300, 400, 500, 600, 800].map((p) => ({ value: p, label: `${gbp(p)} / month` }));
const MILEAGE = [20000, 40000, 60000, 80000, 100000].map((m) => ({ value: m, label: `Up to ${m.toLocaleString("en-GB")}` }));
const SORTS = [
  { value: "newest", label: "Newest first" }, { value: "price_asc", label: "Price low to high" },
  { value: "price_desc", label: "Price high to low" }, { value: "mileage_asc", label: "Mileage low to high" },
];
const FILTER_KEYS = ["make", "model", "price_min", "price_max", "monthly_max", "year_min", "mileage_max", "fuel", "transmission", "body", "seats_min", "category", "towing", "drive"];

export default function Stock({ archive = false, preset = null, heading, intro, seoPath }) {
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [vehicles, setVehicles] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [error, setError] = useState(false);

  const query = useMemo(() => {
    const q = { ...(preset || {}) };
    FILTER_KEYS.concat("sort").forEach((k) => { const v = params.get(k); if (v) q[k] = v; });
    return q;
  }, [params, preset]);
  const active = FILTER_KEYS.filter((k) => params.get(k));

  const finance = useSite().finance_enabled;
  useSeo({
    title: heading ? `${heading} | Cirencester 4x4` : archive ? "Sold vehicles archive | Cirencester 4x4" : "Used 4x4 & SUV Stock in Cirencester | Cirencester 4x4",
    description: intro || "A carefully selected collection of capable, desirable 4x4s and SUVs, inspected and prepared in Cirencester.",
    path: seoPath || (archive ? "/stock/sold" : "/stock"),
  });

  useEffect(() => { api.get("/vehicles/facets").then((r) => setFacets(r?.data || null)).catch(() => {}); }, []);
  useEffect(() => {
    let alive = true;
    setVehicles(null);
    api.get("/vehicles", { params: { ...query, archive } })
      .then((r) => { if (alive) { setVehicles(Array.isArray(r?.data) ? r.data : []); setError(false); } })
      .catch(() => { if (alive) { setVehicles([]); setError(true); } });
    return () => { alive = false; };
  }, [query, archive]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "make") next.delete("model");
    setParams(next, { replace: true });
  };
  const clear = () => { const s = params.get("sort"); setParams(s ? { sort: s } : {}, { replace: true }); };

  return (
    <main className="pt-28 lg:pt-36" data-testid="stock-page">
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <p className="eyebrow text-forest">{archive ? "Archive" : "Stock"}</p>
        <h1 className="mt-4 font-serif text-5xl leading-[0.98] text-charcoal sm:text-6xl lg:text-7xl">{heading || (archive ? "Previously sold" : "Current Stock")}</h1>
        <p className="mt-5 max-w-2xl font-serif text-2xl italic leading-snug text-forest">
          {intro || (archive ? "A record of vehicles we have selected, prepared and sold." : "A carefully selected collection of capable, desirable 4x4s and SUVs.")}
        </p>
      </section>

      <div className="sticky top-20 z-30 mt-12 border-y border-stone bg-paper/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-2 px-5 py-3 sm:gap-4 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setDrawer(true)} className="btn btn-secondary !px-3 !py-2.5 lg:hidden" data-testid="open-filters">
              <SlidersHorizontal className="h-4 w-4" /> Filters{active.length ? ` (${active.length})` : ""}
            </button>
            <p className="whitespace-nowrap text-sm text-charcoal/80" data-testid="results-count" aria-live="polite">{vehicles ? `${vehicles.length} vehicle${vehicles.length === 1 ? "" : "s"}` : "Loading…"}</p>
            {active.length > 0 && <button onClick={clear} className="link-u hidden text-sm font-semibold text-forest sm:inline-block" data-testid="clear-filters-top">Clear filters</button>}
          </div>
          <div className="flex items-center gap-3">
            <label htmlFor="sort" className="sr-only">Sort</label>
            <select id="sort" className="field !w-[8.25rem] !py-2.5 !pl-3 text-sm sm:!w-auto" value={params.get("sort") || "newest"} onChange={(e) => set("sort", e.target.value)} data-testid="sort-select">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-12 sm:px-8 lg:grid-cols-12 lg:px-12">
        <aside className="hidden lg:col-span-3 lg:block" aria-label="Filters">
          <div className="sticky top-44"><Filters facets={facets} params={params} set={set} clear={clear} active={active} /></div>
        </aside>
        <div className="lg:col-span-9">
          {vehicles === null ? (
            <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] animate-pulse bg-stone/60" />)}</div>
          ) : vehicles.length === 0 ? (
            <div className="border border-stone p-12 text-center" data-testid="no-results">
              <p className="font-serif text-3xl text-charcoal">{error ? "Stock is temporarily unavailable." : "No vehicles match those filters."}</p>
              <p className="mt-3 text-charcoal/75">Try widening your search, or tell us what you are looking for and we will keep an eye out.</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={clear} className="btn btn-secondary" data-testid="no-results-clear">Clear filters</button>
                <Link to="/contact" className="btn btn-primary">Contact us</Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 xl:grid-cols-3">
              {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
            </div>
          )}
          {finance && <p className="mt-16 text-xs text-charcoal/70">* Finance figures are illustrative until connected to an approved finance provider.</p>}
          {!archive && <Link to="/stock/sold" className="link-u mt-16 inline-block text-sm font-semibold text-forest" data-testid="sold-archive-link">View sold vehicles archive</Link>}
        </div>
      </section>

      <AnimatePresence>
        {drawer && (
          <motion.div className="fixed inset-0 z-[60] lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button className="absolute inset-0 bg-charcoal/40" aria-label="Close filters" onClick={() => setDrawer(false)} />
            <motion.div data-lenis-prevent role="dialog" aria-modal="true" aria-label="Filters" data-testid="filters-drawer"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
              className="absolute left-0 top-0 flex h-full w-[90%] max-w-sm flex-col bg-paper">
              <div className="flex items-center justify-between border-b border-stone px-6 py-5">
                <p className="font-serif text-2xl">Filters</p>
                <button onClick={() => setDrawer(false)} aria-label="Close" data-testid="close-filters"><X className="h-6 w-6" strokeWidth={1.5} /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-6"><Filters facets={facets} params={params} set={set} clear={clear} active={active} /></div>
              <div className="border-t border-stone p-4">
                <button onClick={() => setDrawer(false)} className="btn btn-primary w-full" data-testid="apply-filters">Show {vehicles?.length ?? ""} vehicles</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function F({ id, label, value, onChange, options, any = "Any" }) {
  return (
    <div>
      <label htmlFor={`f-${id}`} className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-charcoal/80">{label}</label>
      <select id={`f-${id}`} className="field !py-2.5 text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`filter-${id}`}>
        <option value="">{any}</option>
        {options.map((o) => (typeof o === "object" ? <option key={o.value} value={o.value}>{o.label}</option> : <option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}

function Filters({ facets, params, set, clear, active }) {
  const finance = useSite().finance_enabled;
  const g = (k) => params.get(k) || "";
  const make = g("make");
  const models = make ? facets?.models?.[make] || [] : Object.values(facets?.models || {}).flat();
  return (
    <div className="space-y-5" data-testid="filters-panel">
      <div className="flex items-center justify-between">
        <p className="eyebrow text-forest">Refine</p>
        {active.length > 0 && <button onClick={clear} className="link-u text-xs font-semibold uppercase tracking-[0.12em] text-forest" data-testid="clear-filters">Clear all</button>}
      </div>
      <F id="category" label="Category" value={g("category")} onChange={(v) => set("category", v)} options={facets?.categories || []} />
      <F id="make" label="Make" value={make} onChange={(v) => set("make", v)} options={facets?.makes || []} />
      <F id="model" label="Model" value={g("model")} onChange={(v) => set("model", v)} options={models} />
      <div className="grid grid-cols-2 gap-3">
        <F id="price_min" label="Price from" value={g("price_min")} onChange={(v) => set("price_min", v)} options={PRICES} any="No min" />
        <F id="price_max" label="Price to" value={g("price_max")} onChange={(v) => set("price_max", v)} options={PRICES} any="No max" />
      </div>
      {finance && <F id="monthly_max" label="Monthly finance max" value={g("monthly_max")} onChange={(v) => set("monthly_max", v)} options={MONTHLY} />}
      <div className="grid grid-cols-2 gap-3">
        <F id="year_min" label="Year from" value={g("year_min")} onChange={(v) => set("year_min", v)} options={facets?.years || []} />
        <F id="mileage_max" label="Mileage" value={g("mileage_max")} onChange={(v) => set("mileage_max", v)} options={MILEAGE} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="fuel" label="Fuel" value={g("fuel")} onChange={(v) => set("fuel", v)} options={facets?.fuels || []} />
        <F id="transmission" label="Gearbox" value={g("transmission")} onChange={(v) => set("transmission", v)} options={facets?.transmissions || []} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F id="body" label="Body type" value={g("body")} onChange={(v) => set("body", v)} options={facets?.bodies || []} />
        <F id="seats_min" label="Seats" value={g("seats_min")} onChange={(v) => set("seats_min", v)} options={[{ value: 5, label: "5+" }, { value: 6, label: "6+" }, { value: 7, label: "7" }]} />
      </div>
      <F id="drive" label="4WD / AWD" value={g("drive")} onChange={(v) => set("drive", v)} options={facets?.drives || []} />
      <label className="flex cursor-pointer items-center gap-3 border-t border-stone pt-5 text-sm">
        <input type="checkbox" className="h-4 w-4 accent-[#16352C]" checked={g("towing") === "true"} onChange={(e) => set("towing", e.target.checked ? "true" : "")} data-testid="filter-towing" />
        Suitable for towing (3,000kg+)
      </label>
    </div>
  );
}
